import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Plus, GripVertical, Edit, Trash2, Save, Link as LinkIcon, Linkedin, Facebook, Instagram } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

// Social network options
const SOCIAL_NETWORKS = [
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'x', label: 'X (Twitter)' },
  { value: 'meetup', label: 'Meetup' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'github', label: 'GitHub' },
  { value: 'discord', label: 'Discord' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'pinterest', label: 'Pinterest' },
  { value: 'threads', label: 'Threads' },
];

function SortableSocialLink({ link, onUpdate, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: link.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const networkLabel = SOCIAL_NETWORKS.find(n => n.value === link.network)?.label || link.network;

  return (
    <div ref={setNodeRef} style={style} {...attributes} className="flex items-center gap-2 mb-2">
      <div {...listeners} className="cursor-move">
        <GripVertical className="text-gray-400" size={16} />
      </div>
      <Select
        value={link.network}
        onValueChange={(value) => onUpdate(link.id, { ...link, network: value })}
      >
        <SelectTrigger className="w-[140px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SOCIAL_NETWORKS.map(network => (
            <SelectItem key={network.value} value={network.value}>
              {network.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        value={link.url}
        onChange={(e) => onUpdate(link.id, { ...link, url: e.target.value })}
        placeholder="https://..."
        className="flex-1"
      />
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onDelete(link.id)}
        className="text-red-600 hover:text-red-700"
      >
        <Trash2 size={16} />
      </Button>
    </div>
  );
}

function SortableColumn({ column, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <Card className="p-4 mb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            <div {...listeners} className="cursor-move">
              <GripVertical className="text-gray-400" size={20} />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900">{column.title}</h3>
              <p className="text-sm text-gray-500 mt-1">
                {column.links.length} links
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button onClick={() => onEdit(column)} variant="ghost" size="sm">
              <Edit size={18} />
            </Button>
            <Button
              onClick={() => onDelete(column.id)}
              variant="ghost"
              size="sm"
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 size={18} />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function ColumnEditor({ column, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    title: column?.title || '',
    links: column?.links || [],
  });
  const [newLink, setNewLink] = useState({ label: '', url: '' });
  const { toast } = useToast();

  const handleAddLink = () => {
    if (!newLink.label || !newLink.url) {
      toast({
        title: 'Validation Error',
        description: 'Label and URL are required',
        variant: 'destructive',
      });
      return;
    }
    setFormData(prev => ({
      ...prev,
      links: [...prev.links, { ...newLink, order: prev.links.length }]
    }));
    setNewLink({ label: '', url: '' });
  };

  const handleUpdateLink = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      links: prev.links.map((link, i) => 
        i === index ? { ...link, [field]: value } : link
      )
    }));
  };

  const handleRemoveLink = (index) => {
    setFormData(prev => ({
      ...prev,
      links: prev.links.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Column Title
        </label>
        <Input
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          placeholder="Events"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Links
        </label>
        
        {formData.links.map((link, index) => (
          <div key={index} className="flex items-center space-x-2 mb-2">
            <Input 
              value={link.label} 
              onChange={(e) => handleUpdateLink(index, 'label', e.target.value)}
              placeholder="Label"
              className="flex-1" 
            />
            <Input 
              value={link.url} 
              onChange={(e) => handleUpdateLink(index, 'url', e.target.value)}
              placeholder="URL"
              className="flex-1" 
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleRemoveLink(index)}
              className="text-red-600"
            >
              <Trash2 size={16} />
            </Button>
          </div>
        ))}

        <div className="flex items-center space-x-2 mt-4 pt-4 border-t">
          <Input
            value={newLink.label}
            onChange={(e) => setNewLink(prev => ({ ...prev, label: e.target.value }))}
            placeholder="New Link Label"
            className="flex-1"
          />
          <Input
            value={newLink.url}
            onChange={(e) => setNewLink(prev => ({ ...prev, url: e.target.value }))}
            placeholder="URL or #anchor"
            className="flex-1"
          />
          <Button onClick={handleAddLink} variant="outline" size="sm">
            <Plus size={16} />
          </Button>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          onClick={() => onSave(formData)}
          className="bg-black hover:bg-gray-800"
        >
          <Save size={16} className="mr-2" />
          Save Column
        </Button>
      </DialogFooter>
    </div>
  );
}

const FooterEditor = () => {
  const [columns, setColumns] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingColumn, setEditingColumn] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [footerRes, settingsRes] = await Promise.all([
        api.get('/footer'),
        api.get('/settings')
      ]);
      setColumns(footerRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch footer data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings', {
        footer_tagline: settings.footer_tagline,
        social_links: settings.social_links
      });
      toast({ title: 'Success', description: 'Footer settings saved' });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddSocialLink = () => {
    const newLink = {
      id: `temp-${Date.now()}`,
      network: 'linkedin',
      url: '',
      order: settings.social_links?.length || 0
    };
    setSettings(prev => ({
      ...prev,
      social_links: [...(prev.social_links || []), newLink]
    }));
  };

  const handleUpdateSocialLink = (id, updatedLink) => {
    setSettings(prev => ({
      ...prev,
      social_links: prev.social_links.map(link => 
        link.id === id ? updatedLink : link
      )
    }));
  };

  const handleDeleteSocialLink = (id) => {
    setSettings(prev => ({
      ...prev,
      social_links: prev.social_links.filter(link => link.id !== id)
    }));
  };

  const handleSocialDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setSettings(prev => {
        const oldIndex = prev.social_links.findIndex(l => l.id === active.id);
        const newIndex = prev.social_links.findIndex(l => l.id === over.id);
        const newLinks = arrayMove(prev.social_links, oldIndex, newIndex);
        return {
          ...prev,
          social_links: newLinks.map((link, idx) => ({ ...link, order: idx }))
        };
      });
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setColumns((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleCreate = () => {
    setEditingColumn(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (column) => {
    setEditingColumn(column);
    setIsDialogOpen(true);
  };

  const handleSave = async (formData) => {
    try {
      if (editingColumn) {
        await api.put(`/admin/footer/columns/${editingColumn.id}`, {
          title: formData.title,
          order: editingColumn.order
        });

        for (const link of editingColumn.links) {
          await api.delete(`/admin/footer/links/${link.id}`);
        }

        for (const link of formData.links) {
          await api.post('/admin/footer/links', {
            label: link.label,
            url: link.url,
            column_id: editingColumn.id,
            order: link.order,
          });
        }

        toast({ title: 'Success', description: 'Column updated' });
      } else {
        const columnResponse = await api.post('/admin/footer/columns', {
          title: formData.title,
          order: columns.length
        });

        for (const link of formData.links) {
          await api.post('/admin/footer/links', {
            label: link.label,
            url: link.url,
            column_id: columnResponse.data.id,
            order: link.order,
          });
        }

        toast({ title: 'Success', description: 'Column created' });
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save column',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this column?')) return;

    try {
      await api.delete(`/admin/footer/columns/${id}`);
      toast({ title: 'Success', description: 'Column deleted' });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete column',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Footer Editor</h1>
        <p className="text-gray-600 mt-2">
          Manage footer content, social links, and navigation columns
        </p>
      </div>

      <Tabs defaultValue="branding" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="branding">Branding & Social</TabsTrigger>
          <TabsTrigger value="columns">Link Columns</TabsTrigger>
        </TabsList>

        {/* Branding & Social Tab */}
        <TabsContent value="branding">
          <Card className="p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Footer Tagline</h2>
            <p className="text-sm text-gray-500 mb-3">
              Text displayed below the logo in the footer's first column
            </p>
            <Textarea
              value={settings?.footer_tagline || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, footer_tagline: e.target.value }))}
              placeholder="NYC's Premier Tech Community - Connecting innovators, entrepreneurs, and tech enthusiasts."
              rows={3}
            />
          </Card>

          <Card className="p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Social Links</h2>
                <p className="text-sm text-gray-500">
                  Social media buttons shown below the tagline
                </p>
              </div>
              <Button onClick={handleAddSocialLink} variant="outline" size="sm">
                <Plus size={16} className="mr-2" />
                Add Social Link
              </Button>
            </div>

            {settings?.social_links?.length > 0 ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleSocialDragEnd}
              >
                <SortableContext
                  items={settings.social_links.map(l => l.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {settings.social_links.map((link) => (
                    <SortableSocialLink
                      key={link.id}
                      link={link}
                      onUpdate={handleUpdateSocialLink}
                      onDelete={handleDeleteSocialLink}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            ) : (
              <p className="text-gray-500 text-sm py-4">No social links configured. Click "Add Social Link" to get started.</p>
            )}
          </Card>

          <Button onClick={handleSaveSettings} disabled={saving} className="bg-black hover:bg-gray-800">
            <Save size={16} className="mr-2" />
            {saving ? 'Saving...' : 'Save Branding & Social'}
          </Button>

          <div className="mt-8 p-4 bg-gray-50 rounded-lg border">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Note: Footer Legal Links</h3>
            <p className="text-sm text-gray-500">
              Privacy Policy, Terms of Service, and other legal links are managed via the <strong>Menu</strong> tab. 
              Look for the "Footer" menu to add or edit these links.
            </p>
          </div>
        </TabsContent>

        {/* Link Columns Tab */}
        <TabsContent value="columns">
          <div className="flex justify-between items-center mb-6">
            <p className="text-gray-600">
              {columns.length} column{columns.length !== 1 ? 's' : ''} configured
            </p>
            <Button onClick={handleCreate} className="bg-black hover:bg-gray-800">
              <Plus size={20} className="mr-2" />
              Add Column
            </Button>
          </div>

          {columns.length === 0 ? (
            <Card className="p-12 text-center">
              <p className="text-xl text-gray-600 mb-4">No footer columns yet</p>
              <Button onClick={handleCreate} className="bg-black hover:bg-gray-800">
                <Plus size={20} className="mr-2" />
                Create Your First Column
              </Button>
            </Card>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={columns.map((c) => c.id)}
                strategy={verticalListSortingStrategy}
              >
                {columns.map((column) => (
                  <SortableColumn
                    key={column.id}
                    column={column}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingColumn ? 'Edit Column' : 'Create New Column'}
            </DialogTitle>
          </DialogHeader>
          <ColumnEditor
            column={editingColumn}
            onSave={handleSave}
            onCancel={() => setIsDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FooterEditor;
